import { cva } from 'class-variance-authority'

/** What a keyboard focus looks like on anything that takes focus. */
export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

/** The same ring, drawn inside the element — for a row or a pane edge. */
export const FOCUS_RING_INSET = `${FOCUS_RING} focus-visible:ring-inset`

/**
 * Ink and its ground on top of a photograph.
 *
 * `SCRIM` is the wash a caption or a control sits on; `SCRIM_INK` is the ink
 * for the parts that carry text or a stroke. The two tokens behind them do not
 * follow the theme — see `--color-scrim` in `global.css`.
 */
export const SCRIM_INK = 'text-scrim-ink'

export const SCRIM = 'bg-scrim/50'

/** The ground a row takes under the pointer, and the ground it then declares
 *  for whatever floats over it. */
const HOVER_GROUND =
  'hover:bg-line-soft hover:[--row-ground:var(--color-line-soft)]'

/**
 * One line of a list: full width, a hairline under it, a tint while it is the
 * open one, and `relative` for the `SelectionMarker` that marks it.
 *
 * `bg-selected` is the wash, held as a token because the rail draws the same
 * open row at a different geometry and both have to move together.
 *
 * Every list in the Internal area draws it — a table's rows, a navigation
 * column's entries, a log — so the mark that says "this is what the pane is
 * showing" is the same wherever the reader meets it.
 *
 * Ink stays with the caller: a table colours its cells, a navigation entry
 * colours the whole row.
 */
/** How far a list row's content sits from its edges. A heading over a table
 *  takes it too, so its text starts where the rows' does. */
export const ROW_INSET_X = 'px-3.5'

export const listRow = cva(
  // `group/row` and `--row-ground` are what `RowActions` hangs off: the
  // pointer reveals it, and the ground it fades into is whatever the row is
  // wearing at that moment. Declared beside each background so the two cannot
  // part, and named so a descendant's own group cannot fire on row hover.
  'group/row relative flex flex-row items-center gap-2.5 border-b border-line-soft ' +
    `${ROW_INSET_X} py-[7px] text-[13px] [--row-ground:var(--color-card)]`,
  {
    variants: {
      selected: {
        true: 'bg-selected [--row-ground:var(--color-selected)]',
        false: '',
      },
      /** The row is pressed to open something, so it reacts and takes focus. */
      interactive: { true: 'cursor-pointer ' + FOCUS_RING_INSET, false: '' },
      /** Lights up under the pointer without being pressable — a row whose
       *  actions appear there, and which would otherwise reveal them over a
       *  row nothing marks as the one they belong to. */
      hovers: { true: '', false: '' },
      /** A branch drawn as a band over the rows under it. */
      heading: {
        true: 'bg-line-soft [--row-ground:var(--color-line-soft)]',
        false: '',
      },
    },
    compoundVariants: [
      { selected: false, interactive: true, class: HOVER_GROUND },
      { selected: false, hovers: true, class: HOVER_GROUND },
    ],
    defaultVariants: {
      selected: false,
      interactive: false,
      hovers: false,
      heading: false,
    },
  },
)

/**
 * One entry of a menu or a popover list: a glyph, a word, a hover ground.
 *
 * `Menu` builds Radix's own item on it and a popover list uses it on a plain
 * button, so the two read as one vocabulary wherever they meet. The size is
 * the caller's — a kebab menu and a compact popover foot set their own.
 */
export const MENU_ITEM_CLASS =
  'flex flex-row items-center gap-2 rounded-md px-2 py-1.5 text-left'

/** The ground a menu, a popover or a dropdown list floats on. Padding is the
 *  caller's. */
export const FLOATING_SURFACE_CLASS =
  'z-50 rounded-md border border-border bg-card shadow-lg'

/** The hairline between two groups of menu entries. */
export const MENU_SEPARATOR_CLASS = 'my-1 h-px bg-line-soft'

/**
 * The box every chip in a filter row wears.
 *
 * The height is fixed rather than left to the padding: such a row holds chips,
 * a search box and a reset button, and one of them being a pixel taller made
 * the whole bar grow the moment a filter was picked. `FilterChip` and the
 * dashed add button that stands in for an empty bucket both read it, so a full
 * and an empty entry cannot end up on two lines.
 */
export const CHIP_SHAPE =
  'h-7 flex-row items-center gap-1 whitespace-nowrap rounded-md px-[9px] text-xs'

/**
 * A container that scrolls without saying so.
 *
 * For a track that carries its own controls — a carousel with arrows and dots,
 * a row of cards with an arrow at each end. Firefox and WebKit need separate
 * declarations, which is the whole reason this is a constant rather than a
 * class written out each time.
 *
 * Not for a column that only scrolls: there the bar is the sole clue that
 * there is more, and hiding it strands whatever sits below the fold.
 */
export const HIDDEN_SCROLLBAR =
  '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
